import { useRef, useState, ChangeEvent } from 'react';
import { Button, useDataProvider, useNotify, useRefresh } from 'react-admin';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CircularProgress } from '@mui/material';
import { parseSpreadsheet } from '../services/excelImport';

interface Props {
  resource: string;
  mapRow: (row: Record<string, any>) => Record<string, any> | null;
  label?: string;
  onCreated?: (record: Record<string, any>) => Promise<void> | void;
}

const RESOURCE_LABELS: Record<string, string> = {
  debtSlips: 'đơn nợ',
  vatInvoices: 'hóa đơn VAT',
  paymentOrders: 'lệnh chi',
  suppliers: 'nhà cung cấp',
};

// Nút nhập dữ liệu từ Excel/CSV dùng chung trên thanh công cụ.
export function ImportButton({ resource, mapRow, label = 'Nhập Excel', onCreated }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const rows = await parseSpreadsheet(file);
      let n = 0;
      for (const row of rows) {
        const data = mapRow(row);
        if (!data) continue;
        const result = await dataProvider.create(resource, { data });
        if (onCreated) await onCreated(result.data);
        n++;
      }
      notify(`Đã nhập ${n} dòng ${RESOURCE_LABELS[resource] || 'dữ liệu'}`, { type: 'success' });
      refresh();
    } catch {
      notify('Không thể nhập tệp. Vui lòng kiểm tra định dạng và dữ liệu.', { type: 'error' });
    } finally {
      setLoading(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <>
      <Button
        label={label}
        onClick={() => ref.current?.click()}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} /> : <UploadFileIcon />}
      />
      <input ref={ref} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
    </>
  );
}
